import { type FloodAlertsResponse, type UKDefraAlert, fetchUKFloods } from "@/api/fetchFloods";
import type { Feature, GeoJsonProperties, Point } from "geojson";

/**
 * CadentGas-specific enrichment step.
 *
 * What it does:
 * - Fetches UK flood alerts (DEFRA) once.
 * - For each Point feature with a `ta_code` property, looks up a matching flood area id.
 * - Adds/overrides styling-related properties (e.g. `color`, `severity_level`, etc.) on the *same* feature.
 *
 * What it does NOT do:
 * - It does not modify geometries.
 * - It does not create MultiPoint/LineString/etc. All outputs remain Point features.
 *
 * NOTE: This is currently executed in the browser. It should be moved into the geojson backend hydration
 * pipeline to avoid extra client latency and to keep all enrichment server-side.
 */
export function hydratePointFeatures(
  features: Array<Feature<Point, GeoJsonProperties>>,
): Promise<Array<Feature<Point, GeoJsonProperties>>>[] {
  // Fetch once per call.
  const alertsPromise: Promise<FloodAlertsResponse> = fetchUKFloods();

  // Build lookup map once per call.
  const byIdPromise: Promise<Map<string, UKDefraAlert>> = alertsPromise.then(({ alerts }) => {
    const byId = new Map<string, UKDefraAlert>();
    for (const alert of alerts ?? []) {
      const id = alert.properties?.flood_area_id as string | undefined;
      if (!id) {
        continue;
      }
      byId.set(id, alert);
    }
    return byId;
  });

  return features.map(async (feature) => {
    const byId = await byIdPromise;

    const taCode = feature.properties?.ta_code as string | undefined;
    const matched = taCode ? byId.get(taCode) : undefined;

    if (matched) {
      const level = Number(matched.properties?.severity_level);
      let color: string;
      switch (level) {
        case 1:
          color = "red";
          break;
        case 2:
          color = "orange";
          break;
        case 3:
          color = "yellow";
          break;
        default:
          color = "gray";
      }
      // @ts-ignore
      matched.properties = { ...matched.properties, color };
    }

    feature.properties = { ...feature.properties, color: "gray", ...matched?.properties };
    return [feature];
  });
}
